/**
 * WeeklyProgressBar component
 * Displays weekly session progress toward goal with pebbles color theme
 * Wraps a horizontal progress bar with session count label
 */

import { cn } from '@/lib/utils';

export interface WeeklyProgressBarProps {
  /** Current number of sessions completed this week */
  current?: number;
  /** Target number of sessions for the week (defaults to 7) */
  target?: number;
  /** Optional additional class names */
  className?: string;
}

/**
 * WeeklyProgressBar - Shows weekly goal progress with pebbles color
 * Calculates percentage from current/target ratio, caps at 100%
 * Displays "X of Y sessions this week" label
 */
export function WeeklyProgressBar({ current, target, className }: WeeklyProgressBarProps) {
  // Sanitize inputs with defaults: current defaults to 0, target defaults to 7
  const safeCurrent = Math.max(0, Number(current) || 0);
  const safeTarget = Math.max(1, Number(target) || 7);

  // Calculate percentage, capped at 100% to prevent visual overflow
  const percentage = Math.min(100, Math.round((safeCurrent / safeTarget) * 100));

  return (
    <div className={cn('space-y-2', className)}>
      {/* Text label showing current/target */}
      <p className="text-sm text-gray-600">
        <span className="font-semibold text-otter-earth-800">{safeCurrent}</span> of{' '}
        <span className="font-semibold text-otter-earth-800">{safeTarget}</span> sessions this week
      </p>

      {/* Progress bar with pebbles color (bg-pebbles-500) */}
      <div
        className="w-full bg-gray-200 rounded-full h-2"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Weekly progress: ${safeCurrent} of ${safeTarget} sessions`}
      >
        <div
          className="bg-pebbles-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
